import { Paperclip, X } from "lucide-react";
import { useRef, useState } from "react";
import { FloatPanel, StudioButton } from "@/features/studio/theme";

export function CommentComposer({
	onSubmit,
	isSubmitting,
}: {
	onSubmit: (draft: { content: string; file: File | null }) => Promise<{
		success: boolean;
		error?: string;
	}>;
	isSubmitting: boolean;
}): React.ReactElement {
	const [content, setContent] = useState("");
	const [file, setFile] = useState<File | null>(null);
	const [error, setError] = useState<string | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const handleAttach = (event: React.ChangeEvent<HTMLInputElement>) => {
		const selected = event.target.files?.[0];
		if (!selected) return;

		// Keep attachment validation aligned with the backend comments contract.
		const allowed = [
			"image/jpeg",
			"image/png",
			"image/webp",
			"application/pdf",
		];
		if (!allowed.includes(selected.type)) {
			setError("Unsupported attachment type");
			setFile(null);
			event.target.value = "";
			return;
		}
		if (selected.size > 10 * 1024 * 1024) {
			setError("Attachment must be less than 10MB");
			setFile(null);
			event.target.value = "";
			return;
		}

		setError(null);
		setFile(selected);
	};

	const submit = async () => {
		const result = await onSubmit({ content, file });
		if (result.success) {
			setContent("");
			setFile(null);
			setError(null);
			if (inputRef.current) {
				inputRef.current.value = "";
			}
			return;
		}

		setError(result.error || "Failed to submit comment");
	};

	return (
		<FloatPanel elevation={1} padding={20}>
			<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
				<textarea
					value={content}
					onChange={(event) => setContent(event.target.value)}
					placeholder="Write a comment"
					rows={4}
					style={{
						width: "100%",
						minHeight: 80,
						padding: 14,
						border: "1px solid var(--color-border-default)",
						borderRadius: 8,
						background: "var(--color-dashboard-paper)",
						fontFamily: "var(--font-body)",
						fontSize: 14,
						color: "var(--color-dashboard-text)",
						outline: "none",
						resize: "vertical",
						transition: "border-color 150ms ease",
					}}
					onFocus={(e) => {
						e.currentTarget.style.borderColor = "var(--color-dashboard-accent)";
					}}
					onBlur={(e) => {
						e.currentTarget.style.borderColor = "var(--color-border-default)";
					}}
				/>
				{file && (
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							borderRadius: 7,
							border: "1px solid var(--color-dashboard-border)",
							padding: "8px 12px",
							background: "var(--color-dashboard-surface)",
							fontSize: 13,
							color: "var(--color-dashboard-text)",
						}}
					>
						<span
							style={{
								fontWeight: 500,
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
							}}
						>
							{file.name}
						</span>
						<button
							type="button"
							onClick={() => {
								setFile(null);
								if (inputRef.current) {
									inputRef.current.value = "";
								}
							}}
							style={{
								background: "transparent",
								border: 0,
								cursor: "pointer",
								color: "var(--color-dashboard-muted)",
								display: "flex",
								alignItems: "center",
							}}
						>
							<X className="h-4 w-4" />
						</button>
					</div>
				)}
				{error && (
					<p
						className="text-sm text-destructive"
						style={{ color: "var(--color-dashboard-pill-risk)" }}
					>
						{error}
					</p>
				)}
				<input
					ref={inputRef}
					type="file"
					className="hidden"
					accept="image/jpeg,image/png,image/webp,application/pdf"
					onChange={handleAttach}
				/>
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						gap: 10,
						marginTop: 4,
					}}
				>
					<StudioButton
						type="button"
						variant="secondary"
						size="sm"
						icon={Paperclip}
						onClick={() => inputRef.current?.click()}
					>
						Attach file
					</StudioButton>
					<StudioButton
						type="button"
						variant="primary"
						size="sm"
						onClick={submit}
						disabled={isSubmitting}
					>
						Post comment
					</StudioButton>
				</div>
			</div>
		</FloatPanel>
	);
}
